/**
 * Hook que monitoriza novas reservas Box Janeira e dispara notificacoes
 * (som + toast + Notification do SO) em todos os dispositivos da loja.
 *
 * Montado globalmente no AdminDashboard.
 */
import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../services/api';

const STORAGE_KEY = 'box_seen_reservas_v1';
const POLL_INTERVAL_MS = 20000; // 20s

// Gera um duplo bip agradavel via Web Audio API (sem ficheiro externo).
const playDoubleBeep = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const makeBeep = (startAt, freq = 880, durationMs = 180) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + startAt);
      gain.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + startAt + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + startAt + durationMs / 1000);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(ctx.currentTime + startAt);
      osc.stop(ctx.currentTime + startAt + durationMs / 1000 + 0.05);
    };
    makeBeep(0, 880);
    makeBeep(0.26, 1175);
    setTimeout(() => ctx.close().catch(() => {}), 1500);
  } catch (_) { /* noop */ }
};

const loadSeen = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch (_) { return new Set(); }
};
const saveSeen = (setObj) => {
  try {
    // Manter os 200 mais recentes para nao crescer indefinidamente
    const arr = Array.from(setObj).slice(-200);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  } catch (_) { /* noop */ }
};

export default function useBoxReservationWatcher({ enabled = true, onNewReservation } = {}) {
  const seenRef = useRef(loadSeen());
  const firstRunRef = useRef(true);
  const timerRef = useRef(null);
  const onNewRef = useRef(onNewReservation);
  useEffect(() => { onNewRef.current = onNewReservation; }, [onNewReservation]);

  const requestPermission = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  const showOsNotification = useCallback((title, body) => {
    if (typeof window === 'undefined') return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    try {
      const n = new Notification(title, {
        body,
        tag: 'box-janeira-new',
        requireInteraction: false,
      });
      n.onclick = () => {
        try { window.focus(); } catch (_) { /* noop */ }
        n.close();
      };
      setTimeout(() => { try { n.close(); } catch (_) {} }, 12000);
    } catch (_) { /* noop */ }
  }, []);

  const tick = useCallback(async () => {
    try {
      const r = await api.get('/box/reservas?estado=nova&limit=50');
      const novas = Array.isArray(r.data) ? r.data : [];
      const seen = seenRef.current;

      if (firstRunRef.current) {
        // Marcar todas as existentes como "ja vistas" para nao disparar no primeiro load
        novas.forEach(rv => { if (rv?.id) seen.add(rv.id); });
        saveSeen(seen);
        firstRunRef.current = false;
        return;
      }

      const recentes = novas.filter(rv => rv?.id && !seen.has(rv.id));
      if (recentes.length === 0) return;

      recentes.forEach(rv => {
        seen.add(rv.id);
        const loja = rv.loja_nome || rv.loja || '';
        const cliente = [rv.nome, rv.apelido].filter(Boolean).join(' ').trim() || 'Cliente';
        const preco = (rv.preco_aplicado ?? rv.preco ?? 0).toFixed(2).replace('.', ',');
        const msg = `${cliente} — ${loja} — ${preco} EUR`;
        toast.success(`🔔 Nova reserva Box Janeira`, {
          description: msg,
          duration: 10000,
        });
        showOsNotification('Nova reserva Box Janeira', `${msg}${rv.telefone ? `\nTel: ${rv.telefone}` : ''}`);
      });

      playDoubleBeep();
      saveSeen(seen);
      if (typeof onNewRef.current === 'function') {
        onNewRef.current(recentes);
      }
    } catch (_) {
      // silencioso: a app pode estar offline momentaneamente
    }
  }, [showOsNotification]);

  useEffect(() => {
    if (!enabled) return;
    requestPermission();
    // tick inicial
    tick();
    timerRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [enabled, tick, requestPermission]);
}
